import type { ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { useNav } from '../navigation.js';
import { usePermissions } from '../permissions.js';
import { activeSettingsItem, visibleSettings } from './settingsNav.js';

/**
 * The Settings centre shell: a persistent section index beside the active
 * screen, so a person can move between related settings without going back to
 * a menu each time.
 *
 * The index is built from the principal's permissions via `visibleSettings`.
 * Sections the principal cannot read are ABSENT rather than disabled — a
 * greyed-out row advertises the existence of something they may not look at,
 * and invites them to ask why. This mirrors the main rail's rule.
 *
 * The sidebar is presentation. It is not authorization: every screen it links
 * to is independently wrapped in `<RequirePermission>`, and every read and
 * write behind those screens is checked again server-side against the tenant
 * context. Hiding a link has never been, and is not here, a security control.
 */
export function SettingsLayout({ children }: { children: ReactNode }) {
  const navigate = useNav();
  const { permissions, isLoading } = usePermissions();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const sections = visibleSettings(permissions);
  const active = activeSettingsItem(pathname);

  return (
    <div className="set-shell">
      <nav className="set-nav" aria-label="Settings sections">
        <a
          className={`set-nav-home${pathname === '/settings' ? ' is-active' : ''}`}
          href="/settings"
          onClick={(e) => {
            e.preventDefault();
            navigate('/settings');
          }}
          aria-current={pathname === '/settings' ? 'page' : undefined}
        >
          All settings
        </a>
        {/* Same as SettingsIndex: an empty permission set during the first
            load is not the same as an empty one after it. The sidebar says so
            rather than silently showing no sections. */}
        {isLoading && sections.length === 0 ? (
          <p className="set-nav-label" role="status">
            Checking your access…
          </p>
        ) : null}
        {sections.map((section) => (
          // No class: `.set-nav` is a grid whose `gap` already separates the
          // groups, `.set-nav-label` carries its own spacing and `.set-nav ul`
          // its own rules, so this wrapper only exists to key the pair.
          <div key={section.key}>
            <p className="set-nav-label">{section.label}</p>
            <ul>
              {section.items.map((item) => {
                const isActive = active?.key === item.key;
                return (
                  <li key={item.key}>
                    <a
                      href={item.href}
                      className={isActive ? 'is-active' : undefined}
                      aria-current={isActive ? 'page' : undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(item.href);
                      }}
                    >
                      {item.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <div className="set-main">{children}</div>
    </div>
  );
}
